import { ImportSurface, importFileAccept } from 'api-validations/data-import'
import { GeoSpatialFileType } from 'api-validations/locutus'

export const SHAPEFILE_EXTENSIONS = ['dbf', 'shp', 'shx'] as const

/**
 * Without these there is no geometry. The .dbf is only needed for prescriptions.
 */
const REQUIRED_SHAPEFILE_EXTENSIONS = ['shp', 'shx'] as const

type AcceptedFileExtension = (typeof SHAPEFILE_EXTENSIONS)[number] | '2020' | 'zip'

/**
 * One picker serves both asset kinds — a plan takes a boundary and a
 * prescription — so it offers what either surface imports and the parsed file
 * decides which it can be.
 */
export const ACCEPTED_FILE_EXTENSIONS_FOR_INPUT = importFileAccept(
  ImportSurface.Boundaries,
  ImportSurface.Prescriptions
)

/**
 * Extensions a group is named after, in priority order.
 */
const NAMING_EXTENSIONS = ['2020', 'zip', 'shp'] as const

/**
 * A group's name with its extension. Helps a user identify an asset's source.
 */
export const displayName = (file: GeospatialFile) => {
  const extension = NAMING_EXTENSIONS.find((candidate) => candidate in file.files)
  return extension === undefined ? file.name : `${file.name}.${extension}`
}

export const stripFileExtension = (filename: string) => filename.slice(0, filename.lastIndexOf('.'))

export type ErrorMessage = {
  message: { path: string; defaultValue: string; filename?: string }
  extension?: string
}

type FilesShapefile = {
  shp: File
  shx: File
  dbf?: File
}

type FilesZippedShapefile = { zip: File }

type Files2020 = { file: File }

export interface GeospatialFile {
  name: string
  files: Partial<Record<AcceptedFileExtension, File>>
  type: GeoSpatialFileType.ShapeFile | GeoSpatialFileType.Gen3_2020
  errors: ErrorMessage[]
  appendFile(file: File): GeospatialFile
  boundary():
    | {
        valid: true
        data:
          | ({ type: GeoSpatialFileType.ShapeFile } & FilesShapefile)
          | ({ type: GeoSpatialFileType.ShapeFile } & FilesZippedShapefile)
          | ({ type: GeoSpatialFileType.Gen3_2020 } & Files2020)
      }
    | {
        valid: false
        errors: ErrorMessage[]
      }
  prescription():
    | {
        valid: true
        data:
          | ({ type: GeoSpatialFileType.ShapeFile } & FilesShapefile)
          | ({ type: GeoSpatialFileType.ShapeFile } & FilesZippedShapefile)
          | ({ type: GeoSpatialFileType.Gen3_2020 } & Files2020)
      }
    | {
        valid: false
        errors: ErrorMessage[]
      }
}

export const getFileMetadata = (file: File): { name: string; extension: string } => {
  const extensionStart = file.name.lastIndexOf('.')

  return {
    name: file.name.slice(0, extensionStart),
    extension: file.name.slice(extensionStart + 1).toLowerCase()
  }
}

/**
 * Guard to check if a value is one of the elements in an array
 */
export const oneOf = <Arr extends readonly unknown[]>(
  arr: Arr,
  value: unknown
): value is Arr[number] => arr.includes(value)

export class Shapefile implements GeospatialFile {
  name: string
  files: Partial<Record<(typeof SHAPEFILE_EXTENSIONS)[number], File>> = {}
  type: GeoSpatialFileType.ShapeFile = GeoSpatialFileType.ShapeFile
  errors: ErrorMessage[] = []

  constructor(
    options: { file: File } | { name: string; files: Shapefile['files']; errors: ErrorMessage[] }
  ) {
    if ('file' in options) {
      const metadata = getFileMetadata(options.file)
      this.name = metadata.name
      this.appendFile(options.file)
    } else {
      this.name = options.name
      this.files = options.files
      this.errors = options.errors
    }
  }

  appendFile(file: File) {
    this.errors = []
    const metadata = getFileMetadata(file)
    if (metadata.name !== this.name) {
      return new Shapefile({
        name: this.name,
        files: this.files,
        errors: [
          {
            message: {
              path: 'planning.cannotCombineFilesWithDifferentNames',
              defaultValue: 'Cannot combine files with different names'
            }
          }
        ]
      })
    }

    if (metadata.extension in this.files)
      this.errors.push({
        message: {
          path: 'planning.duplicateFile',
          defaultValue: `Duplicate file name: ${this.name}.${metadata.extension}`,
          filename: `${this.name}.${metadata.extension}`
        }
      })
    else if (oneOf(SHAPEFILE_EXTENSIONS, metadata.extension)) this.files[metadata.extension] = file

    for (const extension of REQUIRED_SHAPEFILE_EXTENSIONS)
      if (!(extension in this.files))
        this.errors.push({
          message: {
            path: 'planning.missingFile',
            defaultValue: `Missing file: ${this.name}.${extension}`,
            filename: `${this.name}.${extension}`
          },
          extension
        })

    return new Shapefile({
      name: this.name,
      files: this.files,
      errors: this.errors
    })
  }

  boundary() {
    if (this.files.shp && this.files.shx)
      return {
        valid: true,
        data: {
          type: this.type,
          shp: this.files.shp,
          shx: this.files.shx,
          dbf: this.files.dbf
        }
      } as const

    return { valid: false, errors: this.errors } as const
  }

  prescription() {
    if (this.files.shp && this.files.shx)
      return {
        valid: true,
        data: {
          type: this.type,
          shp: this.files.shp,
          shx: this.files.shx,
          dbf: this.files.dbf
        }
      } as const

    return { valid: false, errors: this.errors } as const
  }
}

export class ZippedShapefile implements GeospatialFile {
  name: string
  files: { zip: File }
  type: GeoSpatialFileType.ShapeFile = GeoSpatialFileType.ShapeFile
  errors: ErrorMessage[] = []

  constructor(
    options:
      | { file: File }
      | { name: string; files: ZippedShapefile['files']; errors: ErrorMessage[] }
  ) {
    if ('file' in options) {
      const metadata = getFileMetadata(options.file)
      this.name = metadata.name

      if (metadata.extension !== 'zip') {
        throw new Error('File must have a .zip extension')
      }
      this.files = { zip: options.file }
    } else {
      this.name = options.name
      this.files = options.files
      this.errors = options.errors
    }
  }

  appendFile(_: File) {
    return new ZippedShapefile({
      name: this.name,
      files: this.files,
      errors: this.errors
    })
  }

  boundary() {
    return {
      valid: true,
      data: {
        type: this.type,
        zip: this.files.zip
      }
    } as const
  }

  prescription() {
    return {
      valid: true,
      data: {
        type: this.type,
        zip: this.files.zip
      }
    } as const
  }
}

export class File2020 implements GeospatialFile {
  name: string
  files: { 2020: File }
  type: GeoSpatialFileType.Gen3_2020 = GeoSpatialFileType.Gen3_2020
  errors: ErrorMessage[] = []

  constructor(
    options: { file: File } | { name: string; files: File2020['files']; errors: ErrorMessage[] }
  ) {
    if ('file' in options) {
      const metadata = getFileMetadata(options.file)
      this.name = metadata.name

      if (metadata.extension !== '2020') {
        throw new Error('File must have a .2020 extension')
      }
      this.files = { 2020: options.file }
    } else {
      this.name = options.name
      this.files = options.files
      this.errors = options.errors
    }
  }

  appendFile(_: File) {
    return new File2020({
      name: this.name,
      files: this.files,
      errors: this.errors
    })
  }

  boundary() {
    return {
      valid: true,
      data: {
        type: this.type,
        file: this.files[2020]
      }
    } as const
  }

  prescription() {
    return {
      valid: true,
      data: {
        type: this.type,
        file: this.files[2020]
      }
    } as const
  }
}

/**
 * Groups picked files into sources: a .2020 or zip stands alone, and shapefile parts
 * group by base name. Unrecognized files are dropped, so a whole folder can be picked.
 */
export const groupPickedFiles = (files: readonly File[]): readonly GeospatialFile[] => {
  const groups = new Map<string, GeospatialFile>()

  for (const file of files) {
    const { name, extension } = getFileMetadata(file)
    if (extension === '2020') groups.set(`${name}.2020`, new File2020({ file }))
    else if (extension === 'zip') groups.set(`${name}.zip`, new ZippedShapefile({ file }))
    else if (oneOf(SHAPEFILE_EXTENSIONS, extension)) {
      const key = `${name}.shp`
      groups.set(key, groups.get(key)?.appendFile(file) ?? new Shapefile({ file }))
    }
  }

  return [...groups.values()]
}
